import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { qboIntegrationApi } from '../api/qboIntegrationApi';
import { tenantQueryKeys } from './useTenantQuery';
import type { QboFormData } from '@xtnd/web/shared/types/tenantSettingTypes';

/**
 * Hook to fetch QBO configuration
 */
export function useQboConfig(enabled: boolean = false) {
  return useQuery({
    queryKey: ['qbo-config'],
    queryFn: () => qboIntegrationApi.getConfig(),
    enabled,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to save QBO configuration
 */
export function useQboConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: QboFormData) => {
      const currentConfig = await tenantSettingsApi.getCurrentTenantConfig();
      const webhookEndpoint = import.meta.env.VITE_API_URL || 'https://api.d.xtnd.legal';

      const qboAPIUpdate: any = {
        clientId: data.clientId,
        realmId: data.realmId,
        baseUrl: 'https://sandbox-quickbooks.api.intuit.com',
        redirectUri: `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/auth/qbo/callback`,
        qboWebhookUri: webhookEndpoint,
        webhooksConfigured: data.enableWebhooks,
        webhookSubscriptions: data.webhookSubscriptions,
        healthCheckEndpoint: '/health/qboAPI',
        settingsComponent: 'QBOConfigModal',
        webhookUrl: `${webhookEndpoint}/webhook/qbo/v1/0000000001`,
      };

      const existingQboModule = currentConfig.modules?.qboAPI;

      if (data.appId && data.appId.trim()) {
        qboAPIUpdate.appId = data.appId;
      } else if (existingQboModule?.appId) {
        qboAPIUpdate.appId = existingQboModule.appId;
      }

      if (data.clientSecret && data.clientSecret.trim()) {
        qboAPIUpdate.clientSecret = data.clientSecret;
      } else if (existingQboModule?.clientSecret) {
        qboAPIUpdate.clientSecret = existingQboModule.clientSecret;
      }

      if (data.accessToken && data.accessToken.trim()) {
        qboAPIUpdate.accessToken = data.accessToken;
      } else if (existingQboModule?.accessToken) {
        qboAPIUpdate.accessToken = existingQboModule.accessToken;
      }

      if (data.refreshToken && data.refreshToken.trim()) {
        qboAPIUpdate.refreshToken = data.refreshToken;
      } else if (existingQboModule?.refreshToken) {
        qboAPIUpdate.refreshToken = existingQboModule.refreshToken;
      }

      if (data.webhookSecret && data.webhookSecret.trim()) {
        qboAPIUpdate.webhookSecret = data.webhookSecret;
      } else if (existingQboModule?.webhookSecret) {
        qboAPIUpdate.webhookSecret = existingQboModule.webhookSecret;
      }

      const { qboAPI: _, ...otherModules } = currentConfig.modules || {};

      const apiPayload = {
        modules: {
          ...otherModules,
          qboAPI: qboAPIUpdate,
        },
      };

      return await tenantSettingsApi.updateCurrentTenantConfig(apiPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.config });
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.configurationStatus });
      showToast.success('QuickBooks Online configuration saved successfully!');
    },
    onError: error => {
      console.error('QBO config save error:', error);
      showToast.error('Failed to save QuickBooks Online configuration');
    },
  });
}

/**
 * Hook to generate QBO OAuth URL
 */
export function useQboOAuthUrlMutation() {
  return useMutation({
    mutationFn: (data: { clientId: string; clientSecret: string; redirectUri: string }) =>
      qboIntegrationApi.generateOAuthUrl(data),
    onError: (error) => {
      console.error('OAuth URL generation failed:', error);
      showToast.error('Failed to generate OAuth URL. Please check your credentials.');
    },
  });
}

/**
 * Hook to test QBO connection
 */
export function useQboTestConnection() {
  return useMutation({
    mutationFn: () => qboIntegrationApi.testConnection(),
    onSuccess: () => {
      showToast.success('QBO connection test successful!');
    },
    onError: (error: any) => {
      console.error('Connection test failed:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Connection test failed';
      showToast.error(`Connection failed: ${errorMessage}`);
    },
  });
}

